/**
 * @file useDeliveryQuote.ts
 * @description Состояние расчёта доставки (phase19.12 шаг 5).
 *
 * Один хук на два потребителя: Sheet «Расчёт доставки» в карточке заказа и
 * справочный калькулятор в настройках перевозчика. Держит форму, результат
 * расчёта и выбранный тариф; сетевые ошибки отдаёт текстом, а «расчёт не
 * получился» — кодом причины от сервера.
 */

import { useCallback, useMemo, useState } from "react";

import { ApiError } from "../../lib/api-client";
import type { OrderDeliveryLineDto } from "../../lib/api-orders";
import {
  quoteCarrierDelivery,
  type CarrierKind,
  type DeliveryQuoteOption,
  type DeliveryQuoteResult,
} from "../../lib/api-shop-carriers";
import {
  buildQuoteInput,
  validateQuoteForm,
  type DeliveryPackageDraft,
  type DeliveryQuoteFormValue,
  type QuoteFormIssue,
} from "../../lib/delivery-quote";

interface Options {
  carrier: CarrierKind;
  initial: DeliveryQuoteFormValue;
  /** Позиции заказа: по ним сервер считает вес одного места. */
  items?: OrderDeliveryLineDto[];
  goodsTotal?: number;
  /** Тариф, который клиент видел на витрине — его выбираем по умолчанию. */
  preferTariffCode?: number | null;
}

export interface DeliveryQuoteController {
  value: DeliveryQuoteFormValue;
  patch: (partial: Partial<DeliveryQuoteFormValue>) => void;
  setPackages: (next: DeliveryPackageDraft[]) => void;
  reset: (next: DeliveryQuoteFormValue) => void;
  quote: DeliveryQuoteResult | null;
  running: boolean;
  /** Ошибка сети/сервера (не «расчёт не выполнен»). */
  error: string | null;
  /** Проблема в форме — с полем для фокуса. */
  issue: QuoteFormIssue | null;
  selected: DeliveryQuoteOption | null;
  select: (option: DeliveryQuoteOption | null) => void;
  run: () => Promise<void>;
}

export function useDeliveryQuote({
  carrier,
  initial,
  items,
  goodsTotal,
  preferTariffCode,
}: Options): DeliveryQuoteController {
  const [value, setValue] = useState<DeliveryQuoteFormValue>(initial);
  const [quote, setQuote] = useState<DeliveryQuoteResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issue, setIssue] = useState<QuoteFormIssue | null>(null);
  const [selectedCode, setSelectedCode] = useState<number | null>(null);

  const patch = useCallback((partial: Partial<DeliveryQuoteFormValue>) => {
    setValue((prev) => ({ ...prev, ...partial }));
  }, []);

  const setPackages = useCallback((next: DeliveryPackageDraft[]) => {
    setValue((prev) => ({ ...prev, packages: next }));
  }, []);

  const reset = useCallback((next: DeliveryQuoteFormValue) => {
    setValue(next);
    setQuote(null);
    setError(null);
    setIssue(null);
    setSelectedCode(null);
  }, []);

  /** Позиции первого места — только их вес сервер считает сам. */
  const quoteItems = useMemo(() => {
    if (!items?.length) return undefined;
    const ids = new Set(value.packages[0]?.orderItemIds ?? []);
    const picked = items.filter((i) => ids.has(i.orderItemId));
    const source = picked.length > 0 ? picked : items;
    return source.map((i) => ({ skuId: i.skuId, quantity: i.quantity }));
  }, [items, value.packages]);

  const run = useCallback(async () => {
    const problem = validateQuoteForm(value, Boolean(quoteItems?.length));
    setIssue(problem);
    if (problem) return;
    setRunning(true);
    setError(null);
    try {
      const result = await quoteCarrierDelivery(
        carrier,
        buildQuoteInput(value, { items: quoteItems, goodsTotal }),
      );
      setQuote(result);
      const options = result.options ?? [];
      const preferred =
        options.find((o) => o.tariffCode === preferTariffCode) ??
        [...options].sort((a, b) => a.price - b.price)[0];
      setSelectedCode(preferred ? preferred.tariffCode : null);
    } catch (err) {
      setQuote(null);
      setError(err instanceof ApiError ? err.message : "Расчёт не выполнен");
    } finally {
      setRunning(false);
    }
  }, [carrier, goodsTotal, preferTariffCode, quoteItems, value]);

  const selected = useMemo(
    () => quote?.options.find((o) => o.tariffCode === selectedCode) ?? null,
    [quote, selectedCode],
  );

  const select = useCallback((option: DeliveryQuoteOption | null) => {
    setSelectedCode(option ? option.tariffCode : null);
  }, []);

  return {
    value,
    patch,
    setPackages,
    reset,
    quote,
    running,
    error,
    issue,
    selected,
    select,
    run,
  };
}
